import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '@/components/home/Navigation';
import PowersureLandingHero from '@/components/home/PowersureLandingHero';
import IntentRebuildHero from '@/components/home/IntentRebuildHero';
import PricingSection from '@/components/home/PricingSection';
import PowersureLandingFinalCTA from '@/components/home/PowersureLandingFinalCTA';
import FooterSection from '@/components/home/FooterSection';
import LanguageSelectionModal from '@/components/LanguageSelectionModal';
import StickyMobileCta from '@/components/home/StickyMobileCta';
import PowersureDeviceGridSection from '@/components/powersure-ui/PowersureDeviceGridSection';
import PowersureFAQSection from '@/components/powersure-ui/PowersureFAQSection';
import PowersureWhatItDoesSection from '@/components/powersure-ui/PowersureWhatItDoesSection';
import PowersureAppPreviewSection from '@/components/powersure-ui/PowersureAppPreviewSection';
import { detectCountryFromIP } from '@/services/geolocation';

/**
 * Powersure Landing — korte verkoopflow (Phase 9.5):
 *   1. Hero               — wat + prijs + CTA boven de fold
 *   2. Wat doet Powersure — 3 dark kaarten
 *   3. App preview        — 3 mobiele mockups (Dashboard / Apparaten / Offertes)
 *   4. Apparaten-grid     — visueel welke apparaten gekoppeld kunnen worden
 *   5. Pricing            — €9,95 / €14,95 hoofdkaart
 *   6. FAQ                — neemt onzekerheid weg
 *   7. Final CTA          — 1x duidelijk
 *
 * Alle eerdere lange tussensecties (Problem / HowItWorks / WhyMeasureFirst /
 * EnergyControlSection / EnergyControlPlan / HomeTypes / WhatYouGet /
 * SamplePreview / Countries / Trust / HomeRecognitionCards) zijn bewust uit
 * de hoofdflow gehaald om de klant binnen 5–10 seconden te laten begrijpen
 * wat Powersure doet. Inhoud die nog nuttig is leeft in FAQ of in de
 * onboarding na CTA-klik.
 */
export default function HomePage() {
  const { i18n } = useTranslation();
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(null);
  // Blok 89 — Intent-Rebuild hero, default OFF. Backed by env
  // PS_LANDING_INTENT_REBUILD_ENABLED on the backend.
  const [intentRebuildEnabled, setIntentRebuildEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/features/public`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d?.landing_intent_rebuild_enabled === true) {
          setIntentRebuildEnabled(true);
        }
      })
      .catch(() => { /* keep default OFF on any error */ });
    return () => { cancelled = true; };
  }, []);

  // Blok 89 — A/B tracker: emit a single `landing_hero_shown` funnel event
  // when the hero is known. Default 'current'; only flips to 'intent_rebuild'
  // once the feature flag has been fetched. No extra cookies. No PII.
  useEffect(() => {
    const variant = intentRebuildEnabled ? 'intent_rebuild' : 'current';
    try {
      const params = new URLSearchParams(window.location.search);
      const session_id = sessionStorage.getItem('powersure_session') || '';
      const body = {
        event: 'landing_hero_shown',
        session_id,
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_term: params.get('utm_term') || '',
        utm_content: params.get('utm_content') || '',
        gclid: params.get('gclid') || '',
        landing_url: window.location.href,
        metadata: { hero_variant: variant },
      };
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/funnel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).catch(() => { /* silent — analytics must not break business flow */ });
    } catch (_) { /* silent */ }
  }, [intentRebuildEnabled]);

  useEffect(() => {
    const initLanguage = async () => {
      const languageSelected = localStorage.getItem('powersure-language-selected');
      if (languageSelected) return;

      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (_) {
        params = null;
      }
      const langParam = params?.get('lang')?.toLowerCase();
      const hasGclid = !!params?.get('gclid');
      const utmSource = params?.get('utm_source')?.toLowerCase();
      const isDutchAdsVisitor =
        langParam === 'nl' || hasGclid || utmSource === 'google';

      if (isDutchAdsVisitor) {
        i18n.changeLanguage('nl');
        localStorage.setItem('powersure-language', 'nl');
        localStorage.setItem('powersure-language-selected', 'auto-nl');
        setDetectedCountry('Nederland');
        return;
      }

      const { country, language } = await detectCountryFromIP();
      setDetectedCountry(country);
      if (language && language !== 'en') {
        i18n.changeLanguage(language);
      }
      if (language === 'nl') {
        localStorage.setItem('powersure-language', 'nl');
        localStorage.setItem('powersure-language-selected', 'auto-ip-nl');
        return;
      }
      setShowLanguageModal(true);
    };
    initLanguage();
    if (window.pwTrack) window.pwTrack('landing_visit');
    // P0 — Ads attribution: fire one `ads_click` event when landing carries
    // a Google click-id (gclid/gbraid/wbraid) or utm_source=google. Done
    // here (not in trackFunnelEvent) so it's idempotent per page load and
    // joins the same session_id as landing_visit/landing_hero_shown.
    try {
      const qs = new URLSearchParams(window.location.search);
      const gclid = qs.get('gclid') || qs.get('gbraid') || qs.get('wbraid') || '';
      const utmSrc = (qs.get('utm_source') || '').toLowerCase();
      const isGoogleAds = !!gclid || utmSrc === 'google' || utmSrc === 'adwords';
      if (isGoogleAds && window.pwTrack) {
        window.pwTrack('ads_click', {
          meta: { gclid: gclid || '', utm_source: utmSrc || '' },
        });
      }
    } catch { /* no-op */ }
  }, [i18n]);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #0a1410 0%, #050a08 100%)' }}>
      <LanguageSelectionModal
        open={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
        detectedCountry={detectedCountry}
      />
      <Navigation />

      {/* 1. Hero — €9,95 + CTA boven de fold (Blok 89 flag-gated) */}
      {intentRebuildEnabled ? (
        <IntentRebuildHero onLanguageClick={() => setShowLanguageModal(true)} />
      ) : (
        <PowersureLandingHero onLanguageClick={() => setShowLanguageModal(true)} />
      )}

      {/* 2. Wat doet Powersure — 3 dark cards */}
      <PowersureWhatItDoesSection />

      {/* 3. Zo ziet Mijn Powersure eruit — 3 mobiele mockups */}
      <PowersureAppPreviewSection />

      {/* 4. Apparaten koppelen — grid (scrollt naar prijs, geen vragen) */}
      <div style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}>
        <PowersureDeviceGridSection
          onAddClick={() => {
            const el = document.querySelector('[data-testid="pricing-section"]');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          title="Welke apparaten u kunt koppelen"
          subtitle="P1, omvormer, laadpaal, warmtepomp, boiler, batterij, slimme stekkers — Powersure ondersteunt de meest gebruikte routes."
        />
      </div>

      {/* 5. Pricing */}
      <PricingSection />

      {/* 6. FAQ */}
      <div style={{ background: 'linear-gradient(180deg, #0a1410 0%, #0e1b16 100%)' }}>
        <PowersureFAQSection testId="home-faq-section" />
      </div>

      {/* 7. Final CTA */}
      <PowersureLandingFinalCTA />

      <FooterSection />
      <StickyMobileCta />
    </div>
  );
}
